import React, { useId, useRef, useState } from "react";

import {
  DEFAULT_THEME_CONFIG,
  generatePalette,
} from "@replohq/lattice/blocks/theme-picker/palette";
import { paletteToCSS } from "@replohq/lattice/blocks/theme-picker/themeCss";
import { Avatar } from "@replohq/lattice/components/avatar/Avatar";
import { Button } from "@replohq/lattice/components/button/Button";
import { toast } from "@replohq/lattice/components/toast/Toast";
import { cn } from "@replohq/lattice/utils/cn";
import { DURATION, EASING } from "@replohq/lattice/utils/motion";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { userRouter, userUtils } from "@v2/trpc/routes/user";
import { Loader2, Upload, X } from "lucide-react";
import {
  animate,
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
} from "motion/react";

// Onboarding pre-renders the default theme before we know the user's system
// preference, so pin to "light" — matches DEFAULT_THEME_CONFIG.mode and keeps
// the setup screen visually stable across devices.
const DEFAULT_PROJECT_THEME_CSS = paletteToCSS(
  generatePalette(DEFAULT_THEME_CONFIG, "light"),
);

const CARD_3D_MAX_ROTATION_DEGREES = 7;
const MAX_AVATAR_BYTES = 3 * 1024 * 1024;

async function preloadImage(imageUrl: string) {
  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    image.addEventListener("load", () => resolve(), { once: true });
    image.addEventListener(
      "error",
      () => reject(new Error("Avatar image failed to load")),
      { once: true },
    );
    image.src = imageUrl;
  });
  await image.decode?.().catch(() => undefined);
}

type AgentSetupCardField = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  autoFocus?: boolean;
};

// (Yuxin, 2026-07-18): onSubmit returns a finalize callback (navigation) on success so the card can finish its animation first, or null to abort.
type AgentSetupCardSubmit = () => Promise<(() => void | Promise<void>) | null>;

type CardFieldProps = AgentSetupCardField & {
  inputRef?: React.Ref<HTMLTextAreaElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement>;
};

function CardField({
  label,
  value,
  onChange,
  placeholder,
  inputRef,
  autoFocus,
  onKeyDown,
}: CardFieldProps) {
  const inputId = useId();
  const captionClass =
    "text-muted opacity-70 transition-opacity duration-fast group-focus-within/field:opacity-100";
  const underlineClass =
    "bg-[color-mix(in_srgb,var(--color-default)_22%,transparent)]";

  return (
    <div className="group/field flex flex-col gap-1">
      <label
        htmlFor={inputId}
        className={cn(
          "font-mono text-[10px] font-medium uppercase leading-none tracking-[0.1em] pl-1",
          captionClass,
        )}
      >
        {label}
      </label>
      <div className="relative">
        <textarea
          id={inputId}
          value={value}
          onChange={(event) => {
            onChange(event.target.value.replace(/[\r\n]+/g, " "));
          }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          ref={inputRef}
          autoFocus={autoFocus}
          rows={1}
          wrap="off"
          className="peer block h-[38px] w-full min-w-0 resize-none appearance-none overflow-hidden border-0 bg-transparent pb-1 pl-2 pr-1 pt-1 font-editorial text-[22px] italic leading-[1.35] text-default outline-none ring-0 placeholder:text-default placeholder:opacity-40 placeholder:italic focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
        />
        <span
          className={cn(
            "pointer-events-none absolute bottom-0 left-0 h-px w-full opacity-40 transition-opacity duration-fast group-focus-within/field:opacity-70 px-1",
            underlineClass,
          )}
        />
        <span
          className={cn(
            "pointer-events-none absolute bottom-0 left-0 h-px w-full origin-left scale-x-0 transition-transform duration-standard ease-out-quint peer-focus:scale-x-100 px-1",
            underlineClass,
          )}
        />
      </div>
    </div>
  );
}

type CardAnimationControls = ReturnType<typeof animate>;

function IdentityCard({
  cardRef,
  cardStyle,
  hoverCardRef,
  onPointerMove,
  onPointerLeave,
  avatarUser,
  avatarAnimationRevision,
  onUploadAvatarClick,
  onDeleteAvatarClick,
  isUpdatingAvatar,
  isDeletingAvatar,
  isAvatarActionDisabled,
  fields,
}: {
  cardRef: React.Ref<HTMLDivElement>;
  cardStyle: React.ComponentProps<typeof motion.div>["style"];
  hoverCardRef: React.Ref<HTMLDivElement>;
  onPointerMove: React.PointerEventHandler<HTMLDivElement>;
  onPointerLeave: React.PointerEventHandler<HTMLDivElement>;
  avatarUser: {
    id: string;
    name: string;
    profileImageUrl: string | null;
  };
  avatarAnimationRevision: number;
  onUploadAvatarClick: () => void;
  onDeleteAvatarClick: () => void;
  isUpdatingAvatar: boolean;
  isDeletingAvatar: boolean;
  isAvatarActionDisabled: boolean;
  fields: CardFieldProps[];
}) {
  const shouldReduceMotion = useReducedMotion();
  const hasProfileImage = Boolean(avatarUser.profileImageUrl);
  const isAvatarLoading = isUpdatingAvatar || isDeletingAvatar;
  const avatarMotionKey = `${avatarUser.id}:${avatarAnimationRevision}`;

  return (
    <motion.div
      ref={cardRef}
      style={cardStyle}
      className="relative h-[400px] w-[340px]"
    >
      <div
        ref={hoverCardRef}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
        className="group/card relative h-full w-full select-none overflow-hidden rounded-[2rem] border-0.5 border-[color-mix(in_srgb,var(--color-border)_82%,var(--color-default)_12%)] bg-surface text-default shadow-[inset_0_1px_0_color-mix(in_srgb,var(--color-sheet)_96%,transparent),0_10px_32px_rgb(0_0_0_/_8%),0_0_0_1px_color-mix(in_srgb,var(--color-default)_7%,transparent)] transition-[box-shadow,transform] duration-fast ease-out hover:shadow-[inset_0_1px_0_color-mix(in_srgb,var(--color-sheet)_98%,transparent),0_14px_40px_rgb(0_0_0_/_10%),0_0_0_1px_color-mix(in_srgb,var(--color-primary)_12%,transparent),0_0_0_3px_color-mix(in_srgb,var(--color-primary)_5%,transparent)] hover:will-change-transform supports-backdrop-filter:backdrop-blur-xl"
      >
        <div className="absolute inset-0 flex flex-col p-6">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-3 rounded-[1.5rem] border border-border/70"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-3 -right-12 h-[224px] w-[224px] bg-primary opacity-[0.08] [mask-image:url('/replo-logo-new-small.svg')] [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]"
          />
          <div className="-mx-6 -mt-6 mb-4 h-12" />

          <div className="mb-6 flex justify-center">
            <div className="group/avatar relative shrink-0">
              <button
                type="button"
                onClick={onUploadAvatarClick}
                disabled={isAvatarActionDisabled}
                aria-label={hasProfileImage ? "Change photo" : "Upload photo"}
                className="relative h-14 w-14 cursor-pointer overflow-hidden rounded-full bg-transparent p-0 ring-1 ring-border transition-transform duration-fast hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-wait disabled:hover:scale-100"
              >
                <AnimatePresence initial={false} mode="popLayout">
                  <motion.div
                    key={avatarMotionKey}
                    initial={
                      shouldReduceMotion
                        ? { opacity: 0 }
                        : { x: "110%", rotate: 130, opacity: 1 }
                    }
                    animate={{ x: "0%", rotate: 0, opacity: 1 }}
                    exit={
                      shouldReduceMotion
                        ? { opacity: 0 }
                        : { x: "-110%", rotate: -130, opacity: 1 }
                    }
                    transition={{
                      duration: shouldReduceMotion ? 0 : DURATION.slow,
                      ease: EASING.inOutQuart,
                    }}
                    className="absolute inset-0"
                  >
                    <Avatar
                      user={avatarUser}
                      size="xl"
                      className="h-full w-full"
                    />
                  </motion.div>
                </AnimatePresence>
                {isAvatarLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center rounded-full bg-black/40">
                    <Loader2 className="size-4 animate-spin text-white drop-shadow-sm" />
                  </div>
                )}
              </button>
              <div className="absolute left-1/2 top-full mt-1.5 flex -translate-x-1/2 items-center gap-0.5 text-muted opacity-0 transition-opacity duration-fast group-hover/avatar:opacity-100 group-focus-within/avatar:opacity-100">
                {hasProfileImage && (
                  <Button
                    variant="tertiary"
                    size="sm"
                    icon
                    onClick={onDeleteAvatarClick}
                    loading={isDeletingAvatar}
                    disabled={isAvatarActionDisabled}
                    aria-label="Remove photo"
                    UNSAFE_className="!bg-transparent !text-current hover:!bg-current/10"
                  >
                    <X />
                  </Button>
                )}
                <Button
                  variant="tertiary"
                  size="sm"
                  icon
                  onClick={onUploadAvatarClick}
                  loading={isUpdatingAvatar}
                  disabled={isAvatarActionDisabled}
                  aria-label="Upload photo"
                  UNSAFE_className="!bg-transparent !text-current hover:!bg-current/10"
                >
                  <Upload />
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {fields.map((field) => (
              <CardField key={field.label} {...field} />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export const AgentSetupCard: React.FC<{
  heading: { title: string; subtitle: string };
  fields: AgentSetupCardField[];
  isFormValid: boolean;
  onSubmit: AgentSetupCardSubmit;
  accountName?: string;
  submitLabel?: string;
  submitDisabled?: boolean;
  onBack?: () => void;
}> = ({
  heading,
  fields,
  isFormValid,
  onSubmit,
  accountName = "",
  submitLabel = "Continue",
  submitDisabled = false,
  onBack,
}) => {
  const { user } = useCurrentUser();
  const shouldReduceMotion = useReducedMotion();
  const avatarActionRunRef = useRef(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const hoverCardRef = useRef<HTMLDivElement>(null);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);
  const cardY = useMotionValue(0);
  const cardScale = useMotionValue(1);
  const cardRotate = useMotionValue(0);
  const cardAnimationControlsRef = useRef<CardAnimationControls[]>([]);
  const cardAnimationRunRef = useRef(0);
  const fieldInputRefs = useRef<(HTMLTextAreaElement | null)[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCardCommitting, setIsCardCommitting] = useState(false);
  const [isShowingLaunchpadLoading, setIsShowingLaunchpadLoading] =
    useState(false);
  const [localAvatarPreviewUrl, setLocalAvatarPreviewUrl] = useState<
    string | null
  >(null);
  const [uploadedAvatar, setUploadedAvatar] = useState<{
    userId: string;
    imageUrl: string;
  } | null>(null);
  const [avatarAnimationRevision, setAvatarAnimationRevision] = useState(0);

  const { mutateAsync: uploadProfilePicture, isPending: isUploadingAvatar } =
    userRouter.uploadProfilePicture.useMutation({
      onSuccess: () => {
        void userUtils.get.invalidate();
      },
    });
  const { mutateAsync: removeProfilePicture, isPending: isRemovingAvatar } =
    userRouter.removeProfilePicture.useMutation({
      onSuccess: () => {
        void userUtils.get.invalidate();
      },
    });

  const isUpdatingAvatar = isUploadingAvatar || isRemovingAvatar;
  const isLoading = isSubmitting || isCardCommitting || isUpdatingAvatar;
  const uploadedAvatarUrl =
    user && uploadedAvatar && uploadedAvatar.userId === user.id
      ? uploadedAvatar.imageUrl
      : null;
  const displayedAvatarUrl =
    localAvatarPreviewUrl ?? uploadedAvatarUrl ?? user?.profileImageUrl ?? null;

  const stopCardCommitAnimations = () => {
    cardAnimationRunRef.current += 1;
    for (const animationControls of cardAnimationControlsRef.current) {
      animationControls.stop();
    }
    cardAnimationControlsRef.current = [];
  };

  const trackCardAnimation = (animationControls: CardAnimationControls) => {
    cardAnimationControlsRef.current.push(animationControls);
    return animationControls;
  };

  const runCardCommitAnimation = async () => {
    stopCardCommitAnimations();
    const animationRun = cardAnimationRunRef.current;
    const isCurrentAnimationRun = () => {
      return cardAnimationRunRef.current === animationRun;
    };

    if (shouldReduceMotion) {
      if (isCurrentAnimationRun()) {
        setIsShowingLaunchpadLoading(true);
      }
      return;
    }

    setIsCardCommitting(true);
    if (hoverCardRef.current) {
      hoverCardRef.current.style.transform = "";
    }
    const cardNode = cardRef.current;
    const distance = cardNode
      ? window.innerHeight - cardNode.getBoundingClientRect().top + 80
      : 800;

    await trackCardAnimation(
      animate(cardScale, 0.96, {
        duration: 0.25,
        ease: EASING.outQuint,
      }),
    );
    if (!isCurrentAnimationRun()) {
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 120));
    if (!isCurrentAnimationRun()) {
      return;
    }

    void trackCardAnimation(
      animate(cardScale, 1, {
        duration: 0.35,
        ease: EASING.outQuint,
      }),
    );
    void trackCardAnimation(
      animate(cardRotate, -4, {
        duration: 1.4,
        ease: EASING.inOutQuart,
      }),
    );
    await trackCardAnimation(
      animate(cardY, distance, {
        duration: 1.4,
        ease: EASING.inOutQuart,
      }),
    );
    if (!isCurrentAnimationRun()) {
      return;
    }

    cardAnimationControlsRef.current = [];
    setIsShowingLaunchpadLoading(true);
  };

  const resetCardCommitAnimation = () => {
    stopCardCommitAnimations();
    cardY.set(0);
    cardScale.set(1);
    cardRotate.set(0);
    setIsShowingLaunchpadLoading(false);
  };

  const handleAvatarFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error({ title: "Keep it under 3MB and try again." });
      event.target.value = "";
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(file);
    const avatarActionRun = avatarActionRunRef.current + 1;
    avatarActionRunRef.current = avatarActionRun;
    setLocalAvatarPreviewUrl(nextPreviewUrl);
    setAvatarAnimationRevision((revision) => revision + 1);

    const cleanupPreview = () => {
      setLocalAvatarPreviewUrl((currentPreviewUrl) => {
        return currentPreviewUrl === nextPreviewUrl ? null : currentPreviewUrl;
      });
      URL.revokeObjectURL(nextPreviewUrl);
    };
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result !== "string") {
        cleanupPreview();
        return;
      }
      const base64 = reader.result.split(",")[1];
      if (!base64) {
        cleanupPreview();
        return;
      }
      void uploadProfilePicture({ imageBase64: base64 }).then(
        async (updatedUser) => {
          if (updatedUser.profileImageUrl) {
            await preloadImage(updatedUser.profileImageUrl).catch(
              () => undefined,
            );
          }
          if (avatarActionRunRef.current !== avatarActionRun) {
            cleanupPreview();
            return;
          }
          setUploadedAvatar(
            updatedUser.profileImageUrl
              ? {
                  userId: updatedUser.id,
                  imageUrl: updatedUser.profileImageUrl,
                }
              : null,
          );
          cleanupPreview();
        },
        () => {
          cleanupPreview();
        },
      );
    });
    reader.addEventListener("error", cleanupPreview);
    reader.addEventListener("abort", cleanupPreview);
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleDeleteAvatar = () => {
    const avatarActionRun = avatarActionRunRef.current + 1;
    avatarActionRunRef.current = avatarActionRun;
    const persistedAvatarUrl = uploadedAvatarUrl ?? user?.profileImageUrl;
    const avatarToRestore =
      user && persistedAvatarUrl
        ? {
            userId: user.id,
            imageUrl: persistedAvatarUrl,
          }
        : null;
    setLocalAvatarPreviewUrl(null);
    setUploadedAvatar(null);
    setAvatarAnimationRevision((revision) => revision + 1);
    void removeProfilePicture({}).catch(() => {
      if (avatarActionRunRef.current !== avatarActionRun) {
        return;
      }
      setUploadedAvatar(avatarToRestore);
      void userUtils.get.invalidate();
    });
  };

  const handleSubmit = async () => {
    if (!isFormValid || isLoading || submitDisabled) {
      return;
    }
    setIsSubmitting(true);
    let didFinalize = false;
    try {
      const cardCommitAnimationPromise = runCardCommitAnimation();
      const finalize = await onSubmit();
      if (finalize) {
        await cardCommitAnimationPromise;
        didFinalize = true;
        await finalize();
      }
    } finally {
      if (!didFinalize) {
        resetCardCommitAnimation();
      }
      setIsSubmitting(false);
      setIsCardCommitting(false);
    }
  };

  const handleFieldKeyDown = (fieldIndex: number) => {
    return (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      if (isFormValid) {
        void handleSubmit();
        return;
      }
      const nextInput = fieldInputRefs.current[fieldIndex + 1];
      nextInput?.focus();
    };
  };

  const cardAccountName = accountName || user?.name || user?.email || "You";

  const handleCardPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (
      shouldReduceMotion ||
      isCardCommitting ||
      event.pointerType !== "mouse"
    ) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const xProgress = (event.clientX - rect.left) / rect.width - 0.5;
    const yProgress = (event.clientY - rect.top) / rect.height - 0.5;
    const rotateX = -yProgress * CARD_3D_MAX_ROTATION_DEGREES;
    const rotateY = xProgress * CARD_3D_MAX_ROTATION_DEGREES;

    event.currentTarget.style.transform = `perspective(900px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-2px)`;
  };

  const resetCardTransform = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.style.transform = "";
  };

  return (
    <div
      className="bg-sheet relative flex h-screen w-full flex-col items-center justify-center gap-8 overflow-y-auto px-6 py-10"
      style={DEFAULT_PROJECT_THEME_CSS}
    >
      <div
        role="img"
        aria-label="Replo"
        className="pointer-events-none absolute left-6 top-6 h-10 w-36 bg-primary [mask-image:url('/images/onboarding/replo-wordmark-black.svg')] [mask-position:left_center] [mask-repeat:no-repeat] [mask-size:contain] md:left-8 md:top-8 md:w-44"
      />
      {onBack && !isCardCommitting && (
        <Button
          variant="tertiary"
          size="sm"
          onClick={onBack}
          disabled={isLoading}
          className="absolute right-6 top-6 md:right-8 md:top-8"
        >
          Back
        </Button>
      )}
      {isShowingLaunchpadLoading ? (
        <div
          aria-busy="true"
          aria-label="Loading onboarding"
          className="flex min-h-[400px] w-[340px] items-center justify-center"
        >
          <span
            className={cn(
              "h-2 w-16 rounded-full bg-[color-mix(in_srgb,var(--color-neutral)_10%,var(--color-sheet))]",
              !shouldReduceMotion && "animate-pulse",
            )}
          />
        </div>
      ) : (
        <div className="flex flex-col items-center gap-6">
          <div className="flex max-w-sm flex-col items-center gap-2 text-center">
            <h1 className="font-editorial text-4xl italic leading-tight text-default">
              {heading.title}
            </h1>
            <p className="text-sm leading-5 text-muted">{heading.subtitle}</p>
          </div>
          <IdentityCard
            cardRef={cardRef}
            cardStyle={{ y: cardY, scale: cardScale, rotate: cardRotate }}
            hoverCardRef={hoverCardRef}
            onPointerMove={handleCardPointerMove}
            onPointerLeave={resetCardTransform}
            avatarUser={{
              id: user?.id ?? "pending",
              name: cardAccountName,
              profileImageUrl: displayedAvatarUrl,
            }}
            avatarAnimationRevision={avatarAnimationRevision}
            onUploadAvatarClick={() => avatarFileInputRef.current?.click()}
            onDeleteAvatarClick={handleDeleteAvatar}
            isUpdatingAvatar={isUploadingAvatar}
            isDeletingAvatar={isRemovingAvatar}
            isAvatarActionDisabled={isLoading}
            fields={fields.map((field, index) => ({
              ...field,
              inputRef: (node: HTMLTextAreaElement | null) => {
                fieldInputRefs.current[index] = node;
              },
              onKeyDown: handleFieldKeyDown(index),
            }))}
          />
          <input
            ref={avatarFileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={handleAvatarFileSelect}
          />
        </div>
      )}

      <div className="flex h-8 items-center">
        <AnimatePresence>
          {isFormValid && !isCardCommitting && (
            <motion.div
              initial={
                shouldReduceMotion
                  ? { opacity: 0 }
                  : { opacity: 0, y: 16, scale: 0.96 }
              }
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={
                shouldReduceMotion
                  ? { opacity: 0 }
                  : { opacity: 0, y: -4, scale: 0.96 }
              }
              transition={{
                duration: shouldReduceMotion ? 0 : DURATION.fast,
                ease: EASING.outQuint,
              }}
            >
              <Button
                variant="primary"
                size="base"
                onClick={() => void handleSubmit()}
                disabled={isLoading || submitDisabled}
                loading={isLoading}
                UNSAFE_className="min-w-[160px]"
              >
                {submitLabel}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
